import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useApp } from "@/context/AppContext";
import { runTopBackHandler } from "@/lib/backButton";
import { isAtTop, scrollToTop } from "@/lib/topRefresh";
import { exitVideoFullscreen } from "@/lib/videoFullscreen";
import { reportClientCrash } from "@/lib/crashReport";
import { guardOn } from "@/lib/guards";

// Wires the Android hardware back button (Capacitor) to sane in-app behaviour:
// 1) close any open overlay (story viewer, comment modal, lightbox…)
// 2) otherwise go back one screen (router history, or fall back to /home)
// 3) ONLY at the home screen → "press again to exit" (double-tap within 2s), then exit.
//    The exit hint is a SELF-CONTAINED fixed banner (not a Sonner toast) so it is
//    guaranteed to appear at the bottom, native-Android style.
// No-op on web/PWA (guarded by Capacitor.isNativePlatform()).
// Lot 687 — robustesse : sans écouteur JS, Capacitor fait « webView.goBack() » (ou rien) → l'enregistrement est
// réessayé s'il échoue ; une étape qui lève une exception n'empêche plus les suivantes ; fil descendu → l'événement
// est annulable (le fil répond `preventDefault`), sinon on remonte directement ; toujours pas en haut 1,5 s plus
// tard → saut direct + diagnostic « back_button » (Admin › Supervision) pour comprendre ce qui a coupé l'animation.
const RETRY_MS = 2000;
const CHECK_MS = 1500;
const safe = (fn) => { try { return !!fn(); } catch { return false; } };
export const isHomePath = (p) => p === "/home" || p === "/";
// Lot 689 — un profil descendu remonte d'abord en haut (comme le fil) ; en haut, Retour quitte la page comme avant.
export const isProfilePath = (p) => typeof p === "string" && /^\/profile\/[^/]+\/?$/.test(p);

// Pure decision for one back press (unit-tested) : "fullscreen" | "overlay" | "back" | "top" | "exit" | "hint".
export const decideBack = ({ path, atTop, lastPress, now }) => {
  if (safe(exitVideoFullscreen)) return "fullscreen";
  if (safe(runTopBackHandler)) return "overlay";
  if (!isHomePath(path) && !isProfilePath(path)) return "back";
  if (!atTop) return "top";
  if (!isHomePath(path)) return "back";
  return now - lastPress < 2000 ? "exit" : "hint";
};

// Page descendue (fil, profil) : prévient la page (annulable) ; non pris en charge → saut direct en haut ; vérification différée.
// Lot 740 — remontée = saut direct (plus d'animation) ; au retour d'une autre page, le fil / profil se recale d'abord sur la
// publication quittée (mémoire de position), un 2e Retour saute à l'en-tête, un 3e (en haut) propose de quitter.
// Lot 695 — le diagnostic n'est envoyé que pour une page qui aurait dû répondre (fil / profil montés) : sur « / »
// (visiteur non connecté : Découvrir / connexion, clavier ouvert…) personne n'écoute, le saut de secours suffit.
export const backToTop = (path = "") => {
  const from = Math.round(window.scrollY || 0);
  const ev = new CustomEvent("yalola:back-to-top", { cancelable: true });
  const handled = !window.dispatchEvent(ev);
  if (!handled) scrollToTop();
  setTimeout(() => {
    if (isAtTop()) return;
    const still = Math.round(window.scrollY || 0);
    try { window.scrollTo({ top: 0, behavior: "instant" }); } catch { /* noop */ }
    if (!handled && path === "/") return;
    reportClientCrash({
      error: { message: `Retour Android sans effet sur ${isProfilePath(path) ? "un profil" : "le fil"} : ${Math.round(from / (window.innerHeight || 800))} écran(s) → toujours à ${Math.round(still / (window.innerHeight || 800))} (page ${handled ? "" : "non "}abonnée)` },
      kind: "back_button",
    });
  }, CHECK_MS);
  return handled;
};

// Lot 702 — « quitter » = passer en ARRIÈRE-PLAN (`minimizeApp`, l'écran reste vivant → retour instantané, « démarrage à
// chaud ») et non plus `exitApp` (écran détruit → le web entier est rechargé au lancement suivant : c'était 44 % de
// « démarrages tièdes lents » dans Android Vitals). Depuis le lot 699 l'app ne consomme rien en arrière-plan. Repli sur
// `exitApp` si la minimisation n'est pas disponible.
// Lot 715 — la veille est désormais derrière le garde-fou admin « back_minimize », OFF par défaut : en production
// (2.4.2, 7 jours) 10 ANR « does not have a focused window » sur TECNO / Infinix / itel (fil principal inactif, Android en
// train de mettre l'activité en pause) — la touche arrivée pendant le retour lent du lanceur est attribuée à Yalola sans
// fenêtre — et ×3 redémarrages inattendus (WebView tuée en arrière-plan). 2.4.1 (exitApp) : 1 ANR sur 170 appareils.
export const leaveApp = async (App, minimize = guardOn("back_minimize")) => {
  if (minimize) {
    try {
      if (typeof App?.minimizeApp === "function") { await App.minimizeApp(); return "minimized"; }
    } catch { /* noop → repli */ }
  }
  try { await App?.exitApp?.(); } catch { /* noop */ }
  return "exited";
};

export const NativeBackButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useApp();
  const lastRef = useRef(0);
  const hideTimer = useRef(null);
  const [showHint, setShowHint] = useState(false);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  // Keep latest navigate/t without re-registering the native listener (which
  // would otherwise leave multiple handlers firing per back press).
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let sub = null;
    let cancelled = false;
    let retry = null;
    const onBack = (App) => {
      const now = Date.now();
      const decision = decideBack({ path: pathRef.current, atTop: isAtTop(), lastPress: lastRef.current, now });
      if (decision === "fullscreen" || decision === "overlay") return;
      if (decision === "back") {
        // Any non-home screen: go back first. Never show the exit hint here.
        const canGoBack = (window.history.state?.idx ?? 0) > 0;
        if (canGoBack) navRef.current(-1); else navRef.current("/home");
        return;
      }
      if (decision === "top") {
        // Lot 661 : fil descendu → Retour remonte en haut (le fil recharge s'il a du nouveau) ; lot 689 : profil descendu idem.
        lastRef.current = 0;
        backToTop(pathRef.current);
        return;
      }
      if (decision === "exit") {
        clearTimeout(hideTimer.current);
        setShowHint(false);
        leaveApp(App);
        return;
      }
      lastRef.current = now;
      setShowHint(true);
      clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setShowHint(false), 2000);
    };
    const register = async () => {
      try {
        const { App } = await import("@capacitor/app");
        const h = await App.addListener("backButton", () => onBack(App));
        if (cancelled) h.remove(); else sub = h;
      } catch (e) {
        if (cancelled) return;
        reportClientCrash({ error: { message: `Bouton Retour Android : écouteur non enregistré (${String(e?.message || e).slice(0, 120)})`, stack: String(e?.stack || "") }, kind: "back_button" });
        retry = setTimeout(register, RETRY_MS);
      }
    };
    register();
    return () => { cancelled = true; clearTimeout(retry); if (sub) sub.remove(); clearTimeout(hideTimer.current); };
  }, []);

  if (!showHint) return null;
  return (
    <div
      data-testid="exit-hint-toast"
      className="fixed left-4 right-4 z-[100000] px-5 py-3.5 rounded-2xl shadow-2xl font-semibold text-sm text-center pointer-events-none animate-in fade-in slide-in-from-bottom-2"
      style={{ bottom: "calc(var(--sa-bottom, 0px) + 24px)", background: "var(--yalola-primary)", color: "#fff" }}
    >
      {tRef.current("app.exit_hint", "Appuyez encore pour quitter")}
    </div>
  );
};

export default NativeBackButton;
